import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { SelectProps } from './Select.types';
import './Select.scss';

export default function Select({
  value, onChange, options, label, placeholder = '—', error, hint,
  disabled, required, className, id, name,
}: SelectProps) {
  const autoId = useId();
  const triggerId = id || `select-${autoId}`;
  const listId = `${triggerId}-listbox`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<number>(() => Math.max(0, options.findIndex(o => o.value === value)));
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find(o => o.value === value) ?? null;

  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, close]);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector<HTMLLIElement>(`[data-idx="${active}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, active]);

  const choose = (i: number) => {
    const opt = options[i];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(i => Math.min(options.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setActive(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!open) setOpen(true); else choose(active); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(options.length - 1); }
  };

  const activeId = open ? `${triggerId}-opt-${active}` : undefined;

  return (
    <div ref={rootRef} className={['select', error ? 'select--error' : '', className || ''].filter(Boolean).join(' ')}>
      {label ? <label className="select__label" htmlFor={triggerId}>{label}{required ? <span className="select__required" aria-hidden="true">*</span> : null}</label> : null}
      <button
        id={triggerId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeId}
        aria-invalid={!!error || undefined}
        disabled={disabled}
        className="select__trigger"
        onClick={() => setOpen(o => !o)}
        onKeyDown={onKey}
        data-name={name}
      >
        <span className={['select__value', !selectedOption ? 'select__value--placeholder' : ''].filter(Boolean).join(' ')}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="select__chev" aria-hidden="true"><ChevronDown size={16} /></span>
      </button>
      {open ? (
        <ul ref={listRef} id={listId} role="listbox" className="select__list" tabIndex={-1}>
          {options.map((opt, i) => (
            <li
              id={`${triggerId}-opt-${i}`}
              key={opt.value}
              data-idx={i}
              role="option"
              aria-selected={value === opt.value}
              aria-disabled={opt.disabled || undefined}
              className={[
                'select__option',
                i === active ? 'select__option--active' : '',
                value === opt.value ? 'select__option--selected' : '',
                opt.disabled ? 'select__option--disabled' : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(i)}
            >
              <span className="select__option-label">{opt.label}</span>
              {value === opt.value ? <Check size={14} aria-hidden="true" /> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <span className="select__error" role="alert">{error}</span> : hint ? <span className="select__hint">{hint}</span> : null}
    </div>
  );
}
